import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import {
  Check,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  Sparkles,
  PartyPopper,
  UserCircle,
  Globe2,
  Copy,
  Link as LinkIcon,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  ONBOARDING_PROGRESS_STEPS,
  OnboardingProgress,
  OnboardingHint as Hint,
} from '@/components/onboarding/OnboardingProgress';
import { organizationService } from '@/services/api/organization.service';
import { bookingService } from '@/services/api/booking.service';
import { staffAvailabilityService } from '@/services/api/availability.service';
import { calendarService } from '@/services/api/calendar.service';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import { getCurrencySymbol, type Currency } from '@/utils/currency';
import { getPublicBookingUrl } from '@/utils/subdomain';
import {
  SUBDOMAIN_PATTERN,
  suggestSubdomain,
  useSubdomainAvailability,
} from '@/hooks/use-subdomain-field';
import type { Organization } from '@/types/organization';

// Step types
type OnboardingStep = 'business' | 'profile' | 'service' | 'launch';

const STEPS: OnboardingStep[] = ['business', 'profile', 'service', 'launch'];

/**
 * The flow used to run seven screens; three of them only carried a field or
 * two. Links and redirects (the Google Calendar round trip especially) still
 * point at the old names, so they are mapped onto the screen that absorbed
 * them rather than dropped.
 */
const LEGACY_STEP_ALIASES: Record<string, OnboardingStep> = {
  welcome: 'business',
  calendar: 'profile',
  availability: 'service',
};

/** Profile fields kept across the trip to Google's consent screen. */
const CALENDAR_DRAFT_KEY = 'sloty.onboarding.profileDraft';

// Duration presets in minutes
const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

export default function OnboardingPage() {
  const { t } = useTranslation(['onboarding', 'common', 'validation', 'dashboard', 'registration']);
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('business');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Service state
  const [serviceName, setServiceName] = useState('');
  const [serviceDuration, setServiceDuration] = useState(60);
  const [servicePrice, setServicePrice] = useState<string>('');
  const [createdServiceId, setCreatedServiceId] = useState<number | null>(null);
  const [currency, setCurrency] = useState<Currency>('USD');
  const [orgData, setOrgData] = useState<Organization | null>(null);

  // Availability state
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Weekdays by default
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  // Business identity state (step 1). Both already exist — Google signup
  // provisions the organization named after the person, on a generated
  // subdomain — so this step edits rather than creates.
  const [organizationName, setOrganizationName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [initialSubdomain, setInitialSubdomain] = useState('');
  const [userEditedSubdomain, setUserEditedSubdomain] = useState(false);

  // Business info state
  const [businessDescription, setBusinessDescription] = useState('');

  // Profile state (fields moved from registration)
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [businessType, setBusinessType] = useState('other');

  // Calendar step state
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);

  // Regional settings state
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('USD');

  // Timezone options
  const BASE_TIMEZONES = [
    { value: 'America/Los_Angeles', labelKey: 'America/Los_Angeles' },
    { value: 'America/Chicago', labelKey: 'America/Chicago' },
    { value: 'America/New_York', labelKey: 'America/New_York' },
    { value: 'America/Anchorage', labelKey: 'America/Anchorage' },
    { value: 'Pacific/Honolulu', labelKey: 'Pacific/Honolulu' },
    { value: 'America/Sao_Paulo', labelKey: 'America/Sao_Paulo' },
    { value: 'America/Manaus', labelKey: 'America/Manaus' },
    { value: 'America/Fortaleza', labelKey: 'America/Fortaleza' },
    { value: 'Europe/London', labelKey: 'Europe/London' },
    { value: 'Europe/Paris', labelKey: 'Europe/Paris' },
    { value: 'Europe/Berlin', labelKey: 'Europe/Berlin' },
    { value: 'Europe/Warsaw', labelKey: 'Europe/Warsaw' },
    { value: 'Europe/Lisbon', labelKey: 'Europe/Lisbon' },
    { value: 'Asia/Tokyo', labelKey: 'Asia/Tokyo' },
    { value: 'Asia/Shanghai', labelKey: 'Asia/Shanghai' },
    { value: 'Australia/Sydney', labelKey: 'Australia/Sydney' },
    { value: 'UTC', labelKey: 'UTC' },
  ];

  // Build timezone list, adding browser-detected if not in base list
  const browserTimezone = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { return 'America/New_York'; }
  })();
  const TIMEZONES = BASE_TIMEZONES.some(tz => tz.value === browserTimezone)
    ? BASE_TIMEZONES
    : [{ value: browserTimezone, labelKey: '__detected__' }, ...BASE_TIMEZONES];

  // Currency options
  const CURRENCIES: { value: Currency; labelKey: string }[] = [
    { value: 'USD', labelKey: 'USD' },
    { value: 'EUR', labelKey: 'EUR' },
    { value: 'BRL', labelKey: 'BRL' },
    { value: 'PLN', labelKey: 'PLN' },
  ];

  // Business types
  const BUSINESS_TYPES = [
    { value: 'salon', labelKey: 'salon' },
    { value: 'spa', labelKey: 'spa' },
    { value: 'clinic', labelKey: 'clinic' },
    { value: 'studio', labelKey: 'studio' },
    { value: 'consulting', labelKey: 'consulting' },
    { value: 'psychotherapy', labelKey: 'psychotherapy' },
    { value: 'other', labelKey: 'other' },
  ];

  // Availability of the booking address, debounced. The organization already
  // owns `initialSubdomain`, so that value comes back "taken" — treated as fine
  // below rather than shown as an error.
  const { available: subdomainAvailable, checking: checkingSubdomain } =
    useSubdomainAvailability(subdomain === initialSubdomain ? undefined : subdomain);

  const subdomainUnchanged = subdomain === initialSubdomain;
  const businessStepValid =
    organizationName.trim().length >= 2 &&
    subdomain.length >= 3 &&
    SUBDOMAIN_PATTERN.test(subdomain) &&
    (subdomainUnchanged || (subdomainAvailable !== false && !checkingSubdomain));

  // Business and profile are the two steps setup cannot do without, so neither
  // can be skipped. A password signup arrives with no name at all — Google
  // sign-ups prefill theirs — so the name is asked for here or nowhere.
  const profileStepValid =
    ownerFirstName.trim().length > 0 && ownerLastName.trim().length > 0;

  // Renaming the business moves the booking address with it, up until the
  // address is edited by hand — after that it stays put. An untouched name
  // keeps the address the signup actually got, which may carry a collision
  // suffix the suggestion would not reproduce.
  useEffect(() => {
    if (userEditedSubdomain) return;
    if (!orgData || organizationName.trim() === (orgData.name || '')) return;

    const suggested = suggestSubdomain(organizationName);
    if (suggested.length >= 3) setSubdomain(suggested);
  }, [organizationName, userEditedSubdomain, orgData]);

  // Check onboarding status on mount
  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  // Prefill the owner's name from the account rather than asking for something
  // we already know. Google sign-ups arrive with it from the Google profile;
  // password sign-ups start blank and simply stay blank.
  //
  // Guarded on the fields still being untouched so a later refresh of `user`
  // cannot overwrite what the person is in the middle of typing.
  useEffect(() => {
    if (!user) return;
    setOwnerFirstName((current) => current || user.firstName || '');
    setOwnerLastName((current) => current || user.lastName || '');
  }, [user]);

  // Returning from the Google consent screen. The calendar connect flow sends
  // the browser back here with ?step=calendar and a status, so pick the step up
  // again and report the outcome — then clear the query so a refresh doesn't
  // replay a stale result.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const step = params.get('step');
    const status = params.get('status');

    const resolvedStep = step
      ? ((STEPS as string[]).includes(step) ? (step as OnboardingStep) : LEGACY_STEP_ALIASES[step])
      : undefined;

    if (resolvedStep) {
      setCurrentStep(resolvedStep);
    }

    if (status === 'success') {
      setCalendarConnected(true);
      restoreProfileDraft();
    } else if (status === 'error') {
      setCalendarError(params.get('message') || t('onboarding:newFlow.calendar.error', 'Could not connect your calendar. Please try again.'));
    }

    if (step || status) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Someone may have connected a calendar already — on a previous visit, or on
  // the trip we just came back from. Don't ask twice.
  useEffect(() => {
    if (currentStep !== 'profile' || calendarConnected) return;

    let cancelled = false;
    calendarService
      .getConnections()
      .then((connections) => {
        if (!cancelled && connections.length > 0) setCalendarConnected(true);
      })
      .catch(() => {
        // Non-fatal: the step still works, it just shows the connect button.
      });

    return () => { cancelled = true; };
  }, [currentStep, calendarConnected]);

  const persistProfileDraft = () => {
    try {
      sessionStorage.setItem(
        CALENDAR_DRAFT_KEY,
        JSON.stringify({
          ownerFirstName,
          ownerLastName,
          businessType,
          selectedTimezone,
          selectedCurrency,
          businessDescription,
        })
      );
    } catch {
      // Storage unavailable (private mode, blocked cookies) — the fields are
      // short and can be retyped; losing them must not break the redirect.
    }
  };

  const restoreProfileDraft = () => {
    try {
      const raw = sessionStorage.getItem(CALENDAR_DRAFT_KEY);
      if (!raw) return;
      sessionStorage.removeItem(CALENDAR_DRAFT_KEY);

      const draft = JSON.parse(raw);
      if (draft.ownerFirstName) setOwnerFirstName(draft.ownerFirstName);
      if (draft.ownerLastName) setOwnerLastName(draft.ownerLastName);
      if (draft.businessType) setBusinessType(draft.businessType);
      if (draft.selectedTimezone) setSelectedTimezone(draft.selectedTimezone);
      if (draft.selectedCurrency) setSelectedCurrency(draft.selectedCurrency);
      if (draft.businessDescription) setBusinessDescription(draft.businessDescription);
    } catch {
      // Corrupt draft: start from the prefilled values instead.
    }
  };

  const handleConnectCalendar = async () => {
    try {
      setIsConnectingCalendar(true);
      setCalendarError(null);

      // return_to brings the browser back to this step rather than the staff
      // calendar settings page.
      // Google takes the whole tab, so anything typed on this step has to
      // survive the round trip.
      persistProfileDraft();

      const { authUrl } = await calendarService.getAuthUrl('onboarding');
      window.location.assign(authUrl);
    } catch (err: any) {
      setCalendarError(sanitizeErrorMessage(err, t('onboarding:newFlow.calendar.error', 'Could not connect your calendar. Please try again.')));
      setIsConnectingCalendar(false);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      setIsLoading(true);
      const [status, org] = await Promise.all([
        organizationService.getOnboardingStatus(),
        organizationService.getOrganization()
      ]);

      if (org) {
        setOrgData(org);
        if (org.currency) {
          setCurrency(org.currency as Currency);
          setSelectedCurrency(org.currency as Currency);
        }
        if (org.timezone) {
          setSelectedTimezone(org.timezone);
        }
        setOrganizationName(org.name || '');
        setSubdomain(org.subdomain || '');
        // Keeps the availability check quiet while the address is untouched:
        // the organization's own subdomain always reads as taken.
        setInitialSubdomain(org.subdomain || '');
      }

      if (status.onboarding.completed || status.onboarding.skipped) {
        setLocation('/admin/dashboard');
        return;
      }

      // The password signup form already asks for the business name and
      // booking address (the provisioning marks that as step 1), so only a
      // Google signup — which gets generated ones — opens on that step.
      if ((status.onboarding.current_step ?? 0) >= 1) {
        setCurrentStep((current) => (current === 'business' ? 'profile' : current));
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Persist current step's data (best-effort) before skipping, so partial
      // progress isn't lost. Errors here don't block the skip itself.
      try {
        if (currentStep === 'service' && serviceName.trim()) {
          // Hours are only saved together with a service: working hours with
          // no service showed up as empty green blocks on the schedule.
          const serviceId = await createService();
          if (serviceId && selectedDays.length > 0 && startTime < endTime) await createAvailability(serviceId);
        }
      } catch (err) {
        console.error('Failed to persist current step before skip:', err);
      }

      await organizationService.skipOnboarding();
      setLocation('/admin/dashboard');
    } catch (error: any) {
      setError(error.response?.data?.message || t('onboarding:errors.skipFailed', 'Failed to skip onboarding'));
    } finally {
      setIsSaving(false);
    }
  };

  const currentStepIndex = STEPS.indexOf(currentStep);

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
      setError(null);
    }
  };

  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
      setError(null);
    }
  };

  const toggleDay = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const createService = async () => {
    // Returns the service id (or null). Retrying after a failed hours save
    // must not create the service twice.
    if (createdServiceId) return createdServiceId;
    if (!serviceName.trim()) {
      setError(t('onboarding:newFlow.service.errors.nameRequired'));
      return null;
    }

    try {
      setIsSaving(true);
      setError(null);

      const priceValue = parseFloat(servicePrice) || 0;
      const service = await bookingService.createService({
        name: serviceName,
        duration: serviceDuration,
        price: priceValue,
        active: true,
      } as any);

      setCreatedServiceId(service.id);
      return service.id;
    } catch (error: any) {
      setError(error.response?.data?.message || t('onboarding:newFlow.service.errors.createFailed'));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Takes the service id explicitly: reading `createdServiceId` state here
  // would see the previous render's value (null) right after createService().
  const createAvailability = async (serviceId: number) => {
    if (selectedDays.length === 0) {
      setError(t('onboarding:newFlow.availability.errors.daysRequired'));
      return false;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Create a recurring availability for the selected days
      await staffAvailabilityService.createMultiDayAvailability({
        days_of_week: selectedDays,
        start_time: startTime,
        end_time: endTime,
        recurrence_type: 'weekly',
        recurrence_interval: 1,
        end_type: 'never',
        service_ids: [serviceId],
      });

      return true;
    } catch (error: any) {
      setError(error.response?.data?.message || t('onboarding:newFlow.availability.errors.createFailed'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      setIsSaving(true);
      setError(null);

      await organizationService.completeOnboarding();
      goToNextStep();
    } catch (error: any) {
      setError(error.response?.data?.message || t('onboarding:errors.completeFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveBusinessIdentity = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updates: Record<string, string> = {};
      if (organizationName.trim() && organizationName.trim() !== orgData?.name) {
        updates.name = organizationName.trim();
      }
      if (subdomain && subdomain !== initialSubdomain) {
        updates.subdomain = subdomain;
      }

      if (Object.keys(updates).length > 0) {
        const org = await organizationService.updateOrganization(updates as any);
        setOrgData(org);
        setInitialSubdomain(org.subdomain || subdomain);
      }

      return true;
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          t('onboarding:newFlow.businessIdentity.errors.saveFailed', 'Could not save your business details. Please try again.')
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveProfile = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const promises: Promise<any>[] = [];

      // Update user profile (first name, last name) via auth/profile endpoint
      const profileUpdates: Record<string, string> = {};
      if (ownerFirstName.trim()) profileUpdates.first_name = ownerFirstName.trim();
      if (ownerLastName.trim()) profileUpdates.last_name = ownerLastName.trim();

      if (Object.keys(profileUpdates).length > 0) {
        const { default: apiClient } = await import('@/lib/api-client');
        promises.push(apiClient.put('/v1/auth/profile', profileUpdates));
      }

      // Update organization business_type + regional settings
      const orgUpdates: Record<string, string> = {};
      if (businessType) orgUpdates.business_type = businessType;
      if (selectedTimezone) orgUpdates.timezone = selectedTimezone;
      if (selectedCurrency) orgUpdates.currency = selectedCurrency;

      if (Object.keys(orgUpdates).length > 0) {
        promises.push(organizationService.updateOrganization(orgUpdates as any));
      }

      // The booking-page description used to be a step of its own; it is one
      // optional field here, saved alongside the rest.
      if (businessDescription.trim()) {
        promises.push(
          organizationService.updateBusinessInfo({ description: businessDescription.trim() })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Update currency state so service step shows correct symbol
      if (selectedCurrency) {
        setCurrency(selectedCurrency);
      }

      return true;
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      setError(error.response?.data?.message || t('onboarding:newFlow.profile.errors.saveFailed', 'Failed to save profile'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    switch (currentStep) {
      case 'business': {
        const identitySaved = await saveBusinessIdentity();
        if (identitySaved) goToNextStep();
        break;
      }
      case 'profile': {
        const profileSaved = await saveProfile();
        if (profileSaved) goToNextStep();
        break;
      }
      case 'service': {
        // Service and hours are one screen now, so they are one save: a
        // service nobody can book is not a finished setup.
        const serviceId = await createService();
        if (!serviceId) break;

        const availabilityCreated = await createAvailability(serviceId);
        if (!availabilityCreated) break;

        await completeOnboarding();
        break;
      }
      case 'launch':
        setLocation('/admin/dashboard');
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  // The account screen is step 1 of the same journey even though it lives on
  // its own route (it runs before an account exists). It is listed here, always
  // behind the current step, so the bar reads the same on both pages.
  const progressSteps = ONBOARDING_PROGRESS_STEPS.map((step) => ({
    id: step.id,
    label: t(step.labelKey, step.fallback),
  }));

  return (
    <div className="min-h-[calc(100vh-4rem)] relative">
      {/* Subtle brand gradient background */}
      <div className="absolute inset-0 pointer-events-none" />

      <div className={cn(
        "relative mx-auto p-4",
        currentStep === 'launch' ? "max-w-3xl" : "max-w-2xl"
      )}>
        <OnboardingProgress steps={progressSteps} current={currentStep} />

        {/* Error display */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/*
          Step: Business — the name clients see and the address they book at.

          Both already exist: signing up with Google provisions the
          organization named after the person, on a generated subdomain. This
          step confirms or replaces them, which is why it can live inside the
          authenticated flow instead of gating it from outside.
        */}
        {currentStep === 'business' && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-purple-500" />
            <div className="px-6 sm:px-10 py-8 sm:py-10 space-y-6">
              {/* Header */}
              <div className="text-center">
                <div className="w-14 h-14 mx-auto bg-orange-100 rounded-2xl flex items-center justify-center mb-3">
                  <Building2 className="h-7 w-7 text-orange-600" />
                </div>
                <h1 className="text-h1 text-ink">
                  {t('onboarding:newFlow.businessIdentity.title', 'Name your business')}
                </h1>
                <p className="text-base text-soft mt-2">
                  {t('onboarding:newFlow.businessIdentity.subtitle', 'This is what clients see, and where they book.')}
                </p>
              </div>

              {/* Form */}
              <div className="bg-surface-2 rounded-xl p-5 sm:p-6 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="organization_name" className="text-sm font-semibold text-ink">
                    {t('registration:fields.businessName', 'Business name')}
                  </Label>
                  <Input
                    id="organization_name"
                    autoFocus
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={t('registration:placeholders.businessName', 'Acme Studio')}
                    className="h-11 bg-surface"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subdomain" className="text-sm font-semibold text-ink">
                    {t('registration:fields.subdomain', 'Booking address')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="subdomain"
                      value={subdomain}
                      onChange={(e) => {
                        setUserEditedSubdomain(true);
                        setSubdomain(e.target.value.toLowerCase());
                      }}
                      className="h-11 bg-surface"
                    />
                    <span className="text-sm text-soft whitespace-nowrap">.slotyapp.com</span>
                  </div>

                  {!subdomainUnchanged && checkingSubdomain && (
                    <p className="flex items-center gap-1 text-sm text-soft">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('registration:subdomain.checking', 'Checking availability...')}
                    </p>
                  )}
                  {!subdomainUnchanged && !checkingSubdomain && subdomainAvailable === true && (
                    <p className="flex items-center gap-1 text-sm text-success">
                      <Check className="h-3 w-3" />
                      {t('registration:subdomain.available', 'Available')}
                    </p>
                  )}
                  {!subdomainUnchanged && !checkingSubdomain && subdomainAvailable === false && (
                    <p className="flex items-center gap-1 text-sm text-danger">
                      <X className="h-3 w-3" />
                      {t('registration:subdomain.taken', 'Already taken')}
                    </p>
                  )}
                  <p className="text-xs text-soft">
                    {t('onboarding:newFlow.businessIdentity.subdomainHint', 'This is the link you share with clients. You can change it later in Settings.')}
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="pt-2">
                <Button
                  onClick={handleNext}
                  disabled={isSaving || !businessStepValid}
                  className="w-full h-11 bg-brand-600 hover:bg-brand-700 shadow-sm shadow-brand-600/20"
                >
                  {isSaving ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t('common:next', 'Next')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/*
          Step: Profile — who you are, how you charge, and the calendar link.

          Calendar used to be a screen of its own. It is a one-click optional
          extra, not a step: it sits at the top of this one, already done for
          anyone who granted the scope while signing up with Google.
        */}
        {currentStep === 'profile' && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-purple-500" />
            <div className="px-6 sm:px-10 py-8 sm:py-10 space-y-6">
              {/* Header */}
              <div className="text-center">
                <div className="w-14 h-14 mx-auto bg-indigo-100 rounded-2xl flex items-center justify-center mb-3">
                  <UserCircle className="h-7 w-7 text-indigo-600" />
                </div>
                <h1 className="text-h1 text-ink">
                  {t('onboarding:newFlow.profile.title', 'First, a bit about you')}
                </h1>
                <p className="text-base text-soft mt-2">
                  {t('onboarding:newFlow.profile.subtitle', 'Your business type helps us show the right examples throughout setup.')}
                </p>
              </div>

              {/* Google Calendar — optional, inline */}
              {calendarConnected ? (
                <div className="flex items-start gap-3 rounded-xl bg-success/10 px-4 py-3">
                  <CheckCircle className="h-5 w-5 mt-0.5 text-success shrink-0" />
                  <p className="text-sm text-ink">
                    {t('onboarding:newFlow.calendar.connected', 'Your Google Calendar is connected. Bookings will appear there automatically.')}
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-brand-200 bg-brand-50 p-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-brand-600 rounded-xl flex items-center justify-center shrink-0">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-brand-900">
                        {t('onboarding:newFlow.calendar.title', 'Connect your Google Calendar')}
                      </p>
                      <p className="text-sm text-brand-800/80 mt-0.5">
                        {t('onboarding:newFlow.calendar.inlineSubtitle', 'Bookings land in your calendar, and busy times are blocked so you never get double-booked.')}
                      </p>
                    </div>
                  </div>

                  {calendarError && (
                    <Alert variant="destructive">
                      <AlertDescription>{calendarError}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleConnectCalendar}
                    disabled={isConnectingCalendar}
                    className="w-full h-11 bg-surface border-brand-300 text-brand-800 hover:bg-surface"
                  >
                    {isConnectingCalendar ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('onboarding:newFlow.calendar.connecting', 'Opening Google...')}
                      </>
                    ) : (
                      <>
                        <Calendar className="mr-2 h-4 w-4" />
                        {t('onboarding:newFlow.calendar.connect', 'Connect Google Calendar')}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-brand-700 text-center">
                    {t('onboarding:newFlow.calendar.optional', 'Optional — you can do this later from Settings.')}
                  </p>
                </div>
              )}

              {/* Form */}
              <div className="bg-surface-2 rounded-xl p-5 sm:p-6 space-y-5">
                {/* First & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-ink">
                      {t('onboarding:newFlow.profile.firstName', 'Your first name')}
                    </Label>
                    <Input
                      required
                      value={ownerFirstName}
                      onChange={(e) => setOwnerFirstName(e.target.value)}
                      placeholder={t('onboarding:newFlow.profile.firstNamePlaceholder', 'John')}
                      className="h-11 bg-surface"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-ink">
                      {t('onboarding:newFlow.profile.lastName', 'Your last name')}
                    </Label>
                    <Input
                      required
                      value={ownerLastName}
                      onChange={(e) => setOwnerLastName(e.target.value)}
                      placeholder={t('onboarding:newFlow.profile.lastNamePlaceholder', 'Doe')}
                      className="h-11 bg-surface"
                    />
                  </div>
                </div>

                {/* Business Type */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.profile.businessType', 'What type of business do you run?')}
                  </Label>
                  <Select value={businessType} onValueChange={setBusinessType}>
                    <SelectTrigger className="h-11 bg-surface">
                      <SelectValue placeholder={t('onboarding:newFlow.profile.businessTypePlaceholder', 'Select business type')} />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {t(`onboarding:newFlow.profile.businessTypes.${type.labelKey}`, type.labelKey.charAt(0).toUpperCase() + type.labelKey.slice(1))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Timezone & currency — detected, confirmed here rather than
                    asked for on a screen of their own. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-ink flex items-center gap-1.5">
                      <Globe2 className="h-3.5 w-3.5 text-soft" />
                      {t('onboarding:newFlow.profile.regional.timezone', 'Your timezone')}
                    </Label>
                    <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                      <SelectTrigger className="h-11 bg-surface">
                        <SelectValue placeholder={t('registration:form.timezonePlaceholder', 'Select timezone')} />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.labelKey === '__detected__'
                              ? `${tz.value.replace(/_/g, ' ')} (${t('registration:timezones.detected', 'Detected')})`
                              : t(`registration:timezones.${tz.labelKey}`, tz.value.replace(/_/g, ' '))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-ink">
                      {t('onboarding:newFlow.profile.regional.currency', 'Your currency')}
                    </Label>
                    <Select value={selectedCurrency} onValueChange={(v) => setSelectedCurrency(v as Currency)}>
                      <SelectTrigger className="h-11 bg-surface">
                        <SelectValue placeholder={t('registration:form.currencyPlaceholder', 'Select currency')} />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((cur) => (
                          <SelectItem key={cur.value} value={cur.value}>
                            {t(`registration:currencies.${cur.labelKey}`, cur.value)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Booking page description — optional, and the last thing left
                    from the step it used to have to itself. */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.business.description', 'Business description')}
                    <span className="text-soft font-normal ml-1">
                      ({t('common:optional', 'optional')})
                    </span>
                  </Label>
                  <Textarea
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                    placeholder={t('onboarding:newFlow.business.descriptionPlaceholder', 'Tell clients what makes your business special...')}
                    rows={3}
                    maxLength={500}
                    className="resize-none bg-surface"
                  />
                  <p className="text-xs text-soft">
                    {t('onboarding:newFlow.business.subtitle', 'This appears on your booking page')}
                  </p>
                </div>

                <p className="text-xs text-soft">
                  {t('onboarding:newFlow.profile.regional.changeNote', 'You can change these anytime in Settings.')}
                </p>
              </div>

              {/* Navigation */}
              <div className="pt-2">
                <Button
                  onClick={handleNext}
                  disabled={isSaving || !profileStepValid}
                  className="w-full h-11 bg-brand-600 hover:bg-brand-700 shadow-sm shadow-brand-600/20"
                >
                  {isSaving ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t('common:next', 'Next')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/*
          Step: Service + hours — what you sell and when you sell it.

          One screen: a service without hours takes no bookings, and hours
          without a service take none either, so neither half is useful alone.
        */}
        {currentStep === 'service' && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-purple-500" />
            <div className="px-6 sm:px-10 py-8 sm:py-10 space-y-6">
              {/* Header */}
              <div className="text-center">
                <div className="w-14 h-14 mx-auto bg-purple-100 rounded-2xl flex items-center justify-center mb-3">
                  <Briefcase className="h-7 w-7 text-purple-600" />
                </div>
                <h1 className="text-h1 text-ink">
                  {t('onboarding:newFlow.service.combinedTitle', 'What you offer, and when')}
                </h1>
                <p className="text-base text-soft mt-2">
                  {t('onboarding:newFlow.service.combinedSubtitle', 'One service and your weekly hours are enough to start taking bookings.')}
                </p>
              </div>

              {/* Contextual hint */}
              <Hint>
                {t('onboarding:newFlow.service.hint', 'Think of the one service you do most often. Start with that — you can always add more later.')}
              </Hint>

              {/* Service */}
              <div className="bg-surface-2 rounded-xl p-5 sm:p-6 space-y-5">
                {/* Service name */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.service.name', 'Service name')}
                  </Label>
                  <Input
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder={t('onboarding:newFlow.service.namePlaceholder', 'e.g., Haircut, Consultation, Massage')}
                    className="h-11 bg-surface"
                  />
                </div>

                {/* Duration */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.service.duration', 'Duration')}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {DURATION_PRESETS.map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setServiceDuration(mins)}
                        className={cn(
                          'px-3 py-2 text-sm rounded-lg border transition-colors',
                          serviceDuration === mins
                            ? 'bg-brand-50 border-brand-500 text-brand-700 font-medium'
                            : 'bg-surface border-line text-soft hover:border-brand-300'
                        )}
                      >
                        {mins < 60 ? `${mins} min` : `${mins / 60}h`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.service.price', 'Price')}
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-soft">
                      {getCurrencySymbol(currency)}
                    </span>
                    <Input
                      type="number"
                      value={servicePrice}
                      onChange={(e) => setServicePrice(e.target.value)}
                      placeholder="0"
                      className="h-11 pl-8 bg-surface"
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <p className="text-xs text-soft">
                    {t('onboarding:newFlow.service.priceHint', "Leave at 0 if you don't want to show pricing")}
                  </p>
                </div>
              </div>

              {/* Weekly hours */}
              <div className="bg-surface-2 rounded-xl p-5 sm:p-6 space-y-5">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.availability.title', 'When are you open for bookings?')}
                  </p>
                </div>

                {/* Day selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.availability.days', 'Working days')}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const labels = t('onboarding:newFlow.availability.daysShort', { returnObjects: true, defaultValue: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] }) as string[];
                      const order = [1, 2, 3, 4, 5, 6, 0];
                      return order.map((dayId) => (
                        <button
                          key={dayId}
                          type="button"
                          onClick={() => toggleDay(dayId)}
                          className={cn(
                            'w-10 h-10 text-sm rounded-lg border transition-colors font-medium',
                            selectedDays.includes(dayId)
                              ? 'bg-brand-50 border-brand-500 text-brand-700'
                              : 'bg-surface border-line text-soft hover:border-brand-300'
                          )}
                        >
                          {labels[dayId]}
                        </button>
                      ));
                    })()}
                  </div>
                </div>

                {/* Time selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-ink">
                    {t('onboarding:newFlow.availability.hours', 'Working hours')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        step={60}
                        lang="en-GB"
                        className="h-11 text-center bg-surface"
                      />
                    </div>
                    <span className="text-soft text-sm">{t('onboarding:newFlow.availability.to', 'to')}</span>
                    <div className="flex-1">
                      <Input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        step={60}
                        lang="en-GB"
                        className="h-11 text-center bg-surface"
                      />
                    </div>
                  </div>
                </div>

                {/* Duration info */}
                {startTime && endTime && startTime < endTime && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-green-800">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">
                        {(() => {
                          const [startH, startM] = startTime.split(':').map(Number);
                          const [endH, endM] = endTime.split(':').map(Number);
                          const totalMins = (endH * 60 + endM) - (startH * 60 + startM);
                          const hours = Math.floor(totalMins / 60);
                          return t('onboarding:newFlow.availability.hoursPerDay', { count: hours, defaultValue: `${hours} hour${hours !== 1 ? 's' : ''} per day` });
                        })()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Time validation warning */}
                {startTime && endTime && startTime >= endTime && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">
                        {t('onboarding:newFlow.availability.timeError', 'End time must be after start time')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={goToPrevStep}
                  className="flex-1 h-11"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('common:back', 'Back')}
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={isSaving || !serviceName.trim() || selectedDays.length === 0 || startTime >= endTime}
                  className="flex-[2] h-11 bg-brand-600 hover:bg-brand-700 shadow-sm shadow-brand-600/20"
                >
                  {isSaving ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t('onboarding:newFlow.business.launch', 'Launch')}
                      <Sparkles className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
              <Button
                variant="link"
                size="sm"
                type="button"
                onClick={handleSkip}
                disabled={isSaving}
                className="w-full"
              >
                {t('onboarding:buttons.skip', 'Skip for now')}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Launch Celebration */}
        {currentStep === 'launch' && (
          <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-purple-500" />
            <div className="px-6 sm:px-10 py-8 sm:py-10 space-y-6 text-center">
              {/* Celebration header */}
              <div>
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-green-500/25">
                  <PartyPopper className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-h1 text-ink">
                  {t('onboarding:newFlow.launch.title', "You're all set!")}
                </h1>
                <p className="text-base text-soft mt-2">
                  {t('onboarding:newFlow.launch.subtitle', 'Your booking page is ready to accept clients')}
                </p>
              </div>

              {/* Summary cards */}
              <div className="bg-surface-2 rounded-xl p-5 sm:p-6 space-y-4 text-left">
                {/* Service created */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                    <Briefcase className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">
                      {t('onboarding:newFlow.launch.serviceCreated', 'Service created')}
                    </p>
                    <p className="text-sm text-soft">
                      {serviceName} · {serviceDuration < 60 ? `${serviceDuration} min` : `${serviceDuration / 60}h`}
                      {parseFloat(servicePrice) > 0 && ` · ${getCurrencySymbol(currency)}${servicePrice}`}
                    </p>
                  </div>
                </div>

                {/* Availability set */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                    <Calendar className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">
                      {t('onboarding:newFlow.launch.availabilitySet', 'Availability set')}
                    </p>
                    <p className="text-sm text-soft">
                      {t('onboarding:newFlow.launch.availabilityDetail', {
                        count: selectedDays.length,
                        start: startTime,
                        end: endTime,
                        defaultValue: '{{count}} days · {{start}} - {{end}}',
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Share your booking page — primary CTA */}
              {orgData?.subdomain && (() => {
                const bookingUrl = getPublicBookingUrl(orgData.subdomain);
                const handleCopy = async () => {
                  try {
                    await navigator.clipboard.writeText(bookingUrl);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch {
                    /* clipboard unavailable — silently ignore */
                  }
                };
                return (
                  <div className="bg-gradient-to-br from-brand-50 to-purple-50 border-2 border-brand-200 rounded-xl p-5 sm:p-6 text-left space-y-4 shadow-sm shadow-brand-500/10">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center shrink-0 shadow-sm shadow-brand-600/30">
                        <LinkIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-brand-900">
                          {t('dashboard:share.title', 'Share your booking page')}
                        </p>
                        <p className="text-sm text-brand-700/80 mt-0.5">
                          {t('dashboard:share.subtitle', 'Let your clients know they can book online')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-surface rounded-lg border border-brand-200 p-2">
                      <span className="flex-1 px-2 text-sm text-ink truncate">
                        {bookingUrl}
                      </span>
                      <Button
                        type="button"
                        onClick={handleCopy}
                        size="sm"
                        className={cn(
                          'h-9 px-3 transition-colors',
                          linkCopied
                            ? 'bg-green-600 hover:bg-green-600 text-white'
                            : 'bg-brand-600 hover:bg-brand-700 text-white'
                        )}
                      >
                        {linkCopied ? (
                          <>
                            <Check className="h-4 w-4 mr-1.5" />
                            {t('dashboard:share.copied', 'Copied!')}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1.5" />
                            {t('dashboard:share.copyLink', 'Copy link')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Next steps */}
              <div className="bg-surface-2 border border-line rounded-xl p-5 sm:p-6 text-left">
                <p className="text-sm font-semibold text-ink mb-3">
                  {t('onboarding:newFlow.launch.nextSteps', 'Next steps')}
                </p>
                <ul className="space-y-2 text-sm text-ink">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-brand-600" />
                    {t('onboarding:newFlow.launch.step1', 'Add more services to your catalog')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-brand-600" />
                    {t('onboarding:newFlow.launch.step2', 'Invite team members')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-brand-600" />
                    {t('onboarding:newFlow.launch.step3', 'Share your booking page')}
                  </li>
                </ul>
              </div>

              {/* CTA button */}
              <Button
                onClick={handleNext}
                className="w-full h-11 bg-brand-600 hover:bg-brand-700 shadow-sm shadow-brand-600/20"
              >
                {t('onboarding:newFlow.launch.goToDashboard', 'Go to Dashboard')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
